'use client';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import Fuse from 'fuse.js';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { ChangeEvent, FC, useMemo, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { Icon, IconButton, Input } from '@/components/ui';
import useProductListingSearchParams from '@/hooks/use-product-listing-search-params';
import { Filter, FilterOption } from '@/types/product-filter';
import { cn } from '@/utils';
import ListItem from './list-item/list-item';

const FilterInterval = dynamic(() => import('./filter-interval/filter-interval'), {
  ssr: false,
});

import useCoreConfig from '@/hooks/use-core-config';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import {
  getProductFilterListHeight,
  PRODUCT_FILTER_ITEM_SIZE_PX,
} from '@/utils/get-product-filter-skeleton-height';
import { getFilterOptionsListKey } from '@/utils/sort-filter-options';

interface Props {
  filter: Filter;
}

const ProductFilter: FC<Props> = ({ filter }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useProductListingSearchParams();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const listRef = useRef<any>(null);

  const sortedOptions = filter.options;

  const fuse = useMemo(
    () =>
      new Fuse(sortedOptions, {
        keys: ['label'],
        threshold: 0.3,
      }),
    [sortedOptions],
  );

  const displayedOptions = useMemo((): FilterOption[] => {
    const query = searchQuery.trim();
    if (!query) {
      return sortedOptions;
    }
    return fuse.search(query).map(result => result.item);
  }, [fuse, searchQuery, sortedOptions]);

  const selectedFilters = searchParams.getAll(filter.id);
  const { keycloak } = useKeycloak();
  const { features } = useCoreConfig();
  const isAuthenticated = keycloak?.authenticated;
  const shouldShowPrice = isAuthenticated || features.priceForGuests;

  if (filter.id === 'price' && !shouldShowPrice) {
    return null;
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    const existingValues = newSearchParams.getAll(filter.id);

    if (filter.id === 'price' && value.includes('-')) {
      newSearchParams.delete('price');
    }

    if (existingValues.includes(value)) {
      newSearchParams.delete(filter.id);
      newSearchParams.set('page', '1');
      existingValues
        .filter(val => val !== value)
        .forEach(val => newSearchParams.append(filter.id, val));
    } else {
      newSearchParams.append(filter.id, value);
      newSearchParams.set('page', '1');
    }
    router.push(`${window.location.pathname}?${newSearchParams.toString()}`);
  };

  const itemSize = PRODUCT_FILTER_ITEM_SIZE_PX;
  const listHeight = getProductFilterListHeight(sortedOptions.length);
  const listKey = getFilterOptionsListKey(filter.id, sortedOptions);

  return (
    <div
      className={cn(
        'mb-6 overflow-auto transition-all duration-500 @lg:overflow-hidden',
        isCollapsed ? 'max-h-8' : `max-h-96`,
      )}
      data-testid="product-filter__container"
    >
      <div>
        <div
          className="hidden items-center justify-between @lg:flex"
          data-testid="product-filter__header"
        >
          <h3 className="font-bold capitalize" data-testid="product-filter__title">
            {filter.name}
          </h3>
          <IconButton
            aria-label={
              isCollapsed ? `Expand ${filter.name} filter` : `Collapse ${filter.name} filter`
            }
            onPress={() => setIsCollapsed(prev => !prev)}
            className="h-6 p-2"
            variant="light"
            data-testid="product-filter__collapse-button"
          >
            {isCollapsed ? <Icon icon={faChevronDown} /> : <Icon icon={faChevronUp} />}
          </IconButton>
        </div>
        {filter.isSearchField && (
          <div className="mb-3 px-2" data-testid="product-filter__search-container">
            <Input
              placeholder={t?.products.search}
              fullWidth
              onChange={handleSearch}
              className="@lg:mt-6"
              classNames={{
                input: 'bg-white',
                inputWrapper: [
                  'bg-white',
                  'group-data-[hover=true]:bg-white',
                  'group-data-[focus=true]:bg-white',
                ],
              }}
              data-testid="product-filter__search-input"
            />
          </div>
        )}
        <div>
          <div
            key={listKey}
            className={cn('hidden overflow-hidden transition-all duration-300 @lg:block')}
            data-testid="product-filter__list-container"
          >
            <FixedSizeList
              ref={listRef}
              height={listHeight}
              width={240}
              itemSize={itemSize}
              itemCount={displayedOptions.length}
              overscanCount={10}
              itemData={{
                options: displayedOptions,
                selectedFilters,
                handleFilterChange,
              }}
              className="scrollbar-thin !overflow-x-hidden"
            >
              {ListItem}
            </FixedSizeList>
          </div>
          <div className="block overflow-auto @lg:hidden">
            {displayedOptions.map((option, index) => (
              <ListItem
                key={option.value}
                index={index}
                style={{ height: 46 }}
                data={{
                  options: displayedOptions,
                  selectedFilters,
                  handleFilterChange,
                }}
                data-testid={`product-filter__mobile-list-item-${index}`}
              />
            ))}
          </div>
        </div>
        {filter.isInterval && (
          <FilterInterval filter={filter} data-testid="product-filter__interval" />
        )}
      </div>
    </div>
  );
};

export default ProductFilter;
